/**
 * useCurrentUser — hook that exposes the cross-subdomain Supabase session
 * to scheduler-side authenticated routes (/me and the /admin pages).
 *
 * Mirrors the portal's AuthProvider but kept lighter-weight here because
 * the scheduler doesn't have react-router; we only have a small number
 * of pages that need auth and can use the hook directly.
 *
 * Returns one of:
 *   { status: 'loading' }
 *   { status: 'unauthenticated' }
 *   { status: 'authenticated', user: { userId, email, advisorId, isAdmin } }
 *
 * The user object joins app_users (advisor link + admin flag) onto
 * auth.users.email so callers can branch on role without a second query.
 */

import { useEffect, useState } from 'react';
import { supabase } from './supabase/client';

export interface AppUser {
  userId: string;
  email: string;
  advisorId: string | null;
  isAdmin: boolean;
}

export type CurrentUserState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; user: AppUser };

const PORTAL_LOGIN_URL = 'https://aaportal.annuityassociation.com/login';

/**
 * If unauthenticated, build a redirect URL that lands the user back on
 * the current scheduler page after they sign in at the portal. Used by
 * page components that want to gate access:
 *
 *   const state = useCurrentUser();
 *   if (state.status === 'unauthenticated') {
 *     window.location.href = redirectToPortalLogin();
 *     return null;
 *   }
 */
export function redirectToPortalLogin(): string {
  if (typeof window === 'undefined') return PORTAL_LOGIN_URL;
  const back = encodeURIComponent(window.location.href);
  return `${PORTAL_LOGIN_URL}?next=${back}`;
}

export function useCurrentUser(): CurrentUserState {
  const [state, setState] = useState<CurrentUserState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function syncFromSession(session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']) {
      if (cancelled) return;
      if (!session) {
        setState({ status: 'unauthenticated' });
        return;
      }

      const { data, error } = await supabase
        .from('app_users')
        .select('advisor_id, is_admin')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (cancelled) return;
      if (error || !data) {
        // Session present but app_users row missing — treat as locked out.
        // Don't sign them out here (the portal owns sign-in/out); just
        // surface unauthenticated and let the caller decide where to
        // redirect.
        console.warn('app_users row missing for', session.user.email, error);
        setState({ status: 'unauthenticated' });
        return;
      }

      setState({
        status: 'authenticated',
        user: {
          userId: session.user.id,
          email: session.user.email ?? '',
          advisorId: (data as { advisor_id: string | null }).advisor_id,
          isAdmin: (data as { is_admin: boolean }).is_admin,
        },
      });
    }

    supabase.auth.getSession().then(({ data }) => {
      void syncFromSession(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void syncFromSession(session);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}
